/**
 * Operations Page
 *
 * Platform Management: the consolidated operations console. Replaces the AI
 * Costs "Triage" + "Adoption" tabs and the old Case Triage page.
 *
 * Three route-driven modes:
 *  - /management/operations                     → Explorer (tiles + table)
 *  - /management/operations/user/:id            → per-operator report
 *  - /management/operations/encounter/:id       → per-encounter report
 *
 * Superadmin only + explicit `platform.triage.view` permission. Read-only;
 * every lookup is audit-logged. PHI: patient IDs only, never names.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  UserIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { AnimatedCard } from '@/components/ui/AnimatedCard';
import { useAuth } from '@/hooks/useAuth';
import {
  triageService,
  TriageReport,
  TriageEncounterReport,
  TriageFinding,
  TriageSeverity,
  RetentionPhase,
  ScopeHierarchy,
  ScopeNode,
  ScopePartner,
  OperationsSummary,
  OperatorStatsRow,
  EncounterExplorerRow,
} from '@/services/triage.service';

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

const fmtDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtDateTime = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '—');

/** Compact duration: minutes → "3d" / "5h" / "12m". */
const fmtMinutes = (m: number | null): string => {
  if (m == null) return '—';
  if (m >= 1440) return `${Math.round((m / 1440) * 10) / 10}d`;
  if (m >= 60) return `${Math.round((m / 60) * 10) / 10}h`;
  return `${Math.round(m)}m`;
};

/** Recording length: seconds → "12m 30s" / "45s". */
const fmtDuration = (seconds: number | null): string => {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const SEVERITY_STYLE: Record<TriageSeverity, { ring: string; chip: string; label: string }> = {
  blocker: { ring: 'border-red-300 bg-red-50', chip: 'bg-red-100 text-red-800', label: 'Blocker' },
  warning: { ring: 'border-amber-300 bg-amber-50', chip: 'bg-amber-100 text-amber-800', label: 'Warning' },
  info: { ring: 'border-slate-200 bg-slate-50', chip: 'bg-slate-100 text-slate-700', label: 'Info' },
};

const SeverityIcon: React.FC<{ severity: TriageSeverity }> = ({ severity }) => {
  if (severity === 'blocker') return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
  if (severity === 'warning') return <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />;
  return <InformationCircleIcon className="h-5 w-5 text-slate-500" />;
};

const StatusPill: React.FC<{ status: string | null }> = ({ status }) => {
  const s = (status || 'unknown').toLowerCase();
  const cls =
    s === 'signed'
      ? 'bg-green-100 text-green-800'
      : s === 'pending'
      ? 'bg-amber-100 text-amber-800'
      : s === 'draft'
      ? 'bg-gray-100 text-gray-700'
      : 'bg-slate-100 text-slate-600';
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{status || '—'}</span>;
};

const RETENTION_STYLE: Record<RetentionPhase, string> = {
  active: 'bg-gray-100 text-gray-700',
  'soft-deleted-recoverable': 'bg-amber-100 text-amber-800',
  'hard-delete-imminent': 'bg-red-100 text-red-800',
  expired: 'bg-red-200 text-red-900',
};

const RetentionPill: React.FC<{ phase: RetentionPhase | null }> = ({ phase }) => {
  if (!phase) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${RETENTION_STYLE[phase]}`}>
      {phase.replace(/-/g, ' ')}
    </span>
  );
};

const shortId = (id: string): string => id.slice(0, 8);

const derivedStatusClass = (s: string): string => {
  if (s === 'signed') return 'bg-green-100 text-green-800';
  if (s === 'note_pending' || s === 'note_draft') return 'bg-amber-100 text-amber-800';
  if (s === 'transcribed' || s === 'transcribing') return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-600';
};

// ---------------------------------------------------------------------------
// Page — route-mode switch
// ---------------------------------------------------------------------------

const Operations: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isSuperadmin = user?.role === 'superadmin';
  const hasPerm = hasPermission('platform.triage.view');

  if (!isSuperadmin || !hasPerm) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-96">
          <div className="text-center max-w-md">
            <LockClosedIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h2 className="mt-3 text-lg font-semibold text-gray-900">Access restricted</h2>
            <p className="mt-1 text-sm text-gray-500">
              {isSuperadmin
                ? 'Operations requires the explicit platform.triage.view permission. Ask an administrator to grant it.'
                : 'Operations is available to platform superadmins only.'}
            </p>
          </div>
        </div>
      </PageTransition>
    );
  }

  if (id && location.pathname.includes('/encounter/')) {
    return <EncounterReportView encounterId={id} />;
  }
  if (id && location.pathname.includes('/user/')) {
    return <OperatorReportView userId={id} />;
  }
  return <ExplorerView />;
};

// ---------------------------------------------------------------------------
// Explorer — tiles + the multi-purpose table
// ---------------------------------------------------------------------------

const WINDOW_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const ExplorerView: React.FC = () => {
  const navigate = useNavigate();

  const [hierarchy, setHierarchy] = useState<ScopeHierarchy | null>(null);
  const [enterpriseId, setEnterpriseId] = useState('all');
  const [partnerId, setPartnerId] = useState('all');
  const [organizationId, setOrganizationId] = useState('all');
  const [windowDays, setWindowDays] = useState(7);
  const [grain, setGrain] = useState<'operators' | 'encounters'>('operators');
  const [q, setQ] = useState('');

  const [summary, setSummary] = useState<OperationsSummary | null>(null);
  const [operators, setOperators] = useState<OperatorStatsRow[]>([]);
  const [encounters, setEncounters] = useState<EncounterExplorerRow[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    triageService
      .getScopeHierarchy()
      .then(setHierarchy)
      .catch(() => setHierarchy({ enterprises: [], orphanPartners: [] }));
  }, []);

  // Summary tiles.
  useEffect(() => {
    let cancelled = false;
    triageService
      .getOperationsSummary({ enterpriseId, partnerId, organizationId }, windowDays)
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, [enterpriseId, partnerId, organizationId, windowDays]);

  // Explorer table.
  useEffect(() => {
    let cancelled = false;
    setTableLoading(true);
    setError(null);
    const scope = { enterpriseId, partnerId, organizationId };
    const load =
      grain === 'operators'
        ? triageService.getOperators(scope, windowDays).then((rows) => {
            if (!cancelled) setOperators(rows);
          })
        : triageService
            .getEncounters(scope, { windowDays: Math.max(windowDays, 30) })
            .then((rows) => {
              if (!cancelled) setEncounters(rows);
            });
    load
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setTableLoading(false); });
    return () => { cancelled = true; };
  }, [enterpriseId, partnerId, organizationId, windowDays, grain]);

  const visiblePartners = useMemo<ScopePartner[]>(() => {
    if (!hierarchy) return [];
    if (enterpriseId !== 'all') {
      return hierarchy.enterprises.find((e) => e.id === enterpriseId)?.partners ?? [];
    }
    return [...hierarchy.enterprises.flatMap((e) => e.partners), ...hierarchy.orphanPartners];
  }, [hierarchy, enterpriseId]);

  const visibleOrganizations = useMemo<ScopeNode[]>(() => {
    if (partnerId !== 'all') {
      return visiblePartners.find((p) => p.id === partnerId)?.organizations ?? [];
    }
    return visiblePartners.flatMap((p) => p.organizations);
  }, [visiblePartners, partnerId]);

  const filteredOperators = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return operators;
    return operators.filter(
      (o) => (o.name ?? '').toLowerCase().includes(needle) || o.email.toLowerCase().includes(needle),
    );
  }, [operators, q]);

  return (
    <PageTransition className="space-y-6" stagger staggerDelay={0.05}>
      <PageTransitionItem>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Operations</h1>
          <p className="mt-2 text-gray-600">
            Operators, encounters and pipeline health in one place. Read-only; every lookup is audit-logged.
          </p>
        </div>
      </PageTransitionItem>

      {/* Filters */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient" hover={false}>
          <div className="flex flex-wrap items-center gap-3 p-4">
            <select
              value={enterpriseId}
              onChange={(e) => { setEnterpriseId(e.target.value); setPartnerId('all'); setOrganizationId('all'); }}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="all">All Enterprises</option>
              {hierarchy?.enterprises.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
            <select
              value={partnerId}
              onChange={(e) => { setPartnerId(e.target.value); setOrganizationId('all'); }}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="all">All Partners</option>
              {visiblePartners.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <select
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="all">All Organizations</option>
              {visibleOrganizations.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <div className="flex-1" />
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-purple-500 focus:ring-purple-500"
            >
              {WINDOW_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Summary tiles */}
      {summary && (
        <PageTransitionItem>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <CountTile label="Encounters" value={String(summary.encounters)} />
            <CountTile label="Recorded → signed" value={`${summary.conversionPercent}%`} />
            <CountTile label="Time to sign p50" value={fmtMinutes(summary.timeToSignP50Minutes)} />
            <CountTile label="Time to sign p95" value={fmtMinutes(summary.timeToSignP95Minutes)} />
            <CountTile
              label="Stuck encounters"
              value={String(summary.stuckCount)}
              tone={summary.stuckCount > 0 ? 'amber' : undefined}
            />
            <CountTile label="Active operators" value={String(summary.activeOperators)} />
          </div>
        </PageTransitionItem>
      )}

      {/* Funnel + org health strip */}
      {summary && (
        <PageTransitionItem>
          <AnimatedCard variant="gradient" hover={false}>
            <div className="flex flex-wrap items-center justify-between gap-4 p-4">
              <div className="flex flex-wrap items-center gap-2">
                {summary.funnelStages.map((s, i) => (
                  <React.Fragment key={s.stage}>
                    {i > 0 && <span className="text-xs text-gray-300">→</span>}
                    <div className="rounded-lg bg-gray-50 px-3 py-1.5 text-center">
                      <div className="text-sm font-semibold text-gray-900">{s.count}</div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">{s.label}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-xs uppercase tracking-wide text-gray-400">Org health</span>
                <span className="font-medium text-green-700">🟢 {summary.orgHealth.green}</span>
                <span className="font-medium text-amber-700">🟡 {summary.orgHealth.yellow}</span>
                <span className="font-medium text-red-700">🔴 {summary.orgHealth.red}</span>
                <span className="font-medium text-gray-400">⚪ {summary.orgHealth.idle}</span>
              </div>
            </div>
          </AnimatedCard>
        </PageTransitionItem>
      )}

      {/* Explorer table */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient" hover={false}>
          <div className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-lg border border-gray-200 p-0.5">
                {(['operators', 'encounters'] as const).map((g) => (
                  <button
                    key={g}
                    onClick={() => setGrain(g)}
                    className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                      grain === g ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {grain === 'operators' && (
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter operators…"
                    className="rounded-md border-gray-300 pl-9 text-sm shadow-sm focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
              )}
            </div>

            {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

            {tableLoading ? (
              <div className="flex items-center justify-center py-12">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="h-8 w-8 rounded-full border-4 border-purple-600 border-t-transparent"
                />
              </div>
            ) : grain === 'operators' ? (
              filteredOperators.length === 0 ? (
                <EmptyRow text="No operators with activity in this window." />
              ) : (
                <TableWrap headers={['Operator', 'Organization', 'Encounters', 'Stuck', 'Signed', 'Completion', 'p50', 'Last active']}>
                  {filteredOperators.map((o) => (
                    <tr
                      key={o.userId}
                      onClick={() => navigate(`/management/operations/user/${o.userId}`)}
                      className="cursor-pointer hover:bg-purple-50"
                    >
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">{o.name || o.email}</div>
                        <div className="text-xs text-gray-400">{o.email}</div>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{o.organizationName || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{o.encountersInWindow}</td>
                      <td className={`px-4 py-2 font-medium ${o.stuckCount > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                        {o.stuckCount}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{o.signedCount}</td>
                      <td className="px-4 py-2 text-gray-600">{Math.round(o.completionRate * 100)}%</td>
                      <td className="px-4 py-2 text-gray-600">{fmtMinutes(o.p50TimeToSignMinutes)}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{fmtDate(o.lastActiveAt)}</td>
                    </tr>
                  ))}
                </TableWrap>
              )
            ) : encounters.length === 0 ? (
              <EmptyRow text="No encounters in this window." />
            ) : (
              <TableWrap headers={['Encounter', 'Patient ID', 'Operator', 'Organization', 'Status', 'Date', 'Duration', 'Age', 'Flags']}>
                {encounters.map((e) => (
                  <tr
                    key={e.encounterId}
                    onClick={() =>
                      navigate(`/management/operations/encounter/${e.encounterId}`, {
                        state: { from: '/management/operations' },
                      })
                    }
                    className="cursor-pointer hover:bg-purple-50"
                  >
                    <td className="px-4 py-2 font-mono text-xs text-purple-700">{shortId(e.encounterId)}</td>
                    <td className="px-4 py-2 text-gray-700">{e.patientId || e.patientMrn || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{e.providerName || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{e.organizationName || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${derivedStatusClass(e.derivedStatus)}`}>
                        {e.derivedStatus}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{fmtDate(e.createdAt)}</td>
                    <td className="px-4 py-2 text-gray-600">{fmtDuration(e.recordingDurationSeconds)}</td>
                    <td className="px-4 py-2 text-gray-600">{fmtMinutes(e.ageMinutes)}</td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        {e.isStuck && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">stuck</span>
                        )}
                        {e.ehrDeliveryStatus === 'failed' && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">EHR failed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </TableWrap>
            )}
          </div>
        </AnimatedCard>
      </PageTransitionItem>
    </PageTransition>
  );
};

// ---------------------------------------------------------------------------
// Operator report — drill-down for one operator
// ---------------------------------------------------------------------------

const OperatorReportView: React.FC<{ userId: string }> = ({ userId }) => {
  const navigate = useNavigate();
  const [report, setReport] = useState<TriageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReport(null);
    triageService
      .getUserReport(userId)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load operator report'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <PageTransition className="space-y-6" stagger staggerDelay={0.05}>
      {loading && (
        <PageTransitionItem>
          <div className="flex items-center justify-center py-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="h-10 w-10 rounded-full border-4 border-purple-600 border-t-transparent"
            />
          </div>
        </PageTransitionItem>
      )}
      {error && !loading && (
        <PageTransitionItem>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">{error}</div>
        </PageTransitionItem>
      )}
      {report && !loading && (
        <TriageReportView report={report} onBack={() => navigate('/management/operations')} />
      )}
    </PageTransition>
  );
};

const TriageReportView: React.FC<{ report: TriageReport; onBack: () => void }> = ({ report, onBack }) => {
  const { user, clinical, findings } = report;

  return (
    <>
      <PageTransitionItem>
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-600 hover:text-purple-700">
          <ArrowLeftIcon className="h-4 w-4" /> Back to Operations
        </button>
      </PageTransitionItem>

      <DiagnosisPanel findings={findings} />

      {/* Account */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient" hover={false}>
          <div className="p-5">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
              <UserIcon className="h-5 w-5 text-purple-600" />
              {user.firstName} {user.lastName}
            </h2>
            <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              <Field label="Email" value={user.email} />
              <Field label="Role" value={user.role} />
              <Field label="Organization" value={user.organizationName || '—'} />
              <Field label="Last login" value={fmtDateTime(user.account.lastLogin)} />
              <Field label="Failed login attempts" value={String(user.account.loginAttempts)} />
              <Field
                label="Account state"
                value={
                  user.isDeleted ? 'Deleted' : !user.isActive ? 'Inactive' : user.account.isLocked ? 'Locked' : 'Active'
                }
                tone={user.isDeleted || !user.isActive ? 'red' : user.account.isLocked ? 'amber' : 'green'}
              />
              {user.account.isLocked && (
                <Field label="Locked until" value={fmtDateTime(user.account.lockUntil)} tone="red" />
              )}
              <Field
                label="Password reset"
                value={
                  user.account.passwordResetToken
                    ? `Pending (expires ${fmtDateTime(user.account.passwordResetExpires)})`
                    : 'None'
                }
              />
              <Field label="Last password change" value={fmtDateTime(user.account.lastPasswordChange)} />
            </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Counts */}
      <PageTransitionItem>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <CountTile label="Notes (signed / total)" value={`${clinical.counts.notesSigned} / ${clinical.counts.notesTotal}`} />
          <CountTile label="Clinical encounters" value={String(clinical.counts.encountersTotal)} />
          <CountTile label="Recordings (active)" value={String(clinical.counts.recordingsActive)} />
          <CountTile
            label="Recordings expiring ≤5d"
            value={String(clinical.counts.recordingsExpiringSoon)}
            tone={clinical.counts.recordingsExpiringSoon > 0 ? 'amber' : undefined}
          />
        </div>
      </PageTransitionItem>

      {/* Recordings — retention / blob lifecycle */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient" hover={false}>
          <div className="p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              Recordings — retention &amp; audio blob
            </h2>
            {clinical.recordings.length === 0 ? (
              <EmptyRow text="No recordings for this operator." />
            ) : (
              <TableWrap headers={['Recording', 'Patient ID', 'Status', 'Retention', 'Soft-delete', 'Hard-delete']}>
                {clinical.recordings.map((r) => (
                  <tr key={r.recordingId}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{shortId(r.recordingId)}</td>
                    <td className="px-4 py-2 text-gray-700">{r.patientId || r.patientMrn || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{r.status || '—'}</td>
                    <td className="px-4 py-2"><RetentionPill phase={r.retentionPhase} /></td>
                    <td className="px-4 py-2 text-gray-600">
                      {fmtDate(r.softDeleteAt)}
                      {r.daysUntilSoftDelete !== null && !r.isDeleted && (
                        <span className="ml-1 text-xs text-gray-400">({r.daysUntilSoftDelete}d)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {fmtDate(r.hardDeleteAt)}
                      {r.daysUntilHardDelete !== null && (
                        <span className="ml-1 text-xs text-gray-400">({r.daysUntilHardDelete}d)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </TableWrap>
            )}
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Notes */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient" hover={false}>
          <div className="p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Medical notes</h2>
            {clinical.notes.length === 0 ? (
              <EmptyRow text="No notes for this operator." />
            ) : (
              <TableWrap headers={['Note', 'Patient ID', 'Status', 'Signed', 'Updated', '']}>
                {clinical.notes.map((n) => (
                  <tr key={n.noteId}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{shortId(n.noteId)}</td>
                    <td className="px-4 py-2 text-gray-700">{n.patientId || n.patientMrn || '—'}</td>
                    <td className="px-4 py-2"><StatusPill status={n.status} /></td>
                    <td className="px-4 py-2 text-gray-600">{fmtDate(n.signedAt)}</td>
                    <td className="px-4 py-2 text-gray-600">{fmtDate(n.updatedAt)}</td>
                    <td className="px-4 py-2 text-xs text-gray-400">{n.isDeleted ? 'deleted' : ''}</td>
                  </tr>
                ))}
              </TableWrap>
            )}
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Clinical encounters */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient" hover={false}>
          <div className="p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Clinical encounters</h2>
            {clinical.clinicalEncounters.length === 0 ? (
              <EmptyRow text="No clinical encounters for this operator." />
            ) : (
              <TableWrap headers={['Encounter', 'Patient ID', 'Status', 'EHR delivery', 'Signed']}>
                {clinical.clinicalEncounters.map((e) => (
                  <tr key={e.encounterId}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{shortId(e.encounterId)}</td>
                    <td className="px-4 py-2 text-gray-700">{e.patientId || e.patientMrn || '—'}</td>
                    <td className="px-4 py-2"><StatusPill status={e.status} /></td>
                    <td className="px-4 py-2 text-gray-600">{e.ehrDeliveryStatus || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{fmtDate(e.signedAt)}</td>
                  </tr>
                ))}
              </TableWrap>
            )}
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Connected encounters */}
      {clinical.connectedEncounters.length > 0 && (
        <PageTransitionItem>
          <AnimatedCard variant="gradient" hover={false}>
            <div className="p-5">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Connected (EHR) encounters
              </h2>
              <TableWrap headers={['Encounter', 'Patient ID', 'Note status', 'Retention expires', 'Days left']}>
                {clinical.connectedEncounters.map((e) => (
                  <tr key={e.encounterId}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{shortId(e.encounterId)}</td>
                    <td className="px-4 py-2 text-gray-700">{e.patientId || e.patientMrn || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{e.clinicalNoteStatus || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{fmtDate(e.retentionExpiresAt)}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {e.retentionDaysRemaining !== null ? `${e.retentionDaysRemaining}d` : '—'}
                    </td>
                  </tr>
                ))}
              </TableWrap>
            </div>
          </AnimatedCard>
        </PageTransitionItem>
      )}

      <PageTransitionItem>
        <p className="text-center text-xs text-gray-400">
          Generated {fmtDateTime(report.generatedAt)} · PHI level: {report.meta.phiLevel} · read-only
          {report.meta.truncated && ' · some lists truncated to the 200 most recent rows'}
        </p>
      </PageTransitionItem>
    </>
  );
};

// ---------------------------------------------------------------------------
// Encounter report — drill-down for one clinical encounter
// ---------------------------------------------------------------------------

const EncounterReportView: React.FC<{ encounterId: string }> = ({ encounterId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [report, setReport] = useState<TriageEncounterReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReport(null);
    triageService
      .getEncounterReport(encounterId)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load encounter report'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [encounterId]);

  const goBack = () => {
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from ?? '/management/operations');
  };

  return (
    <PageTransition className="space-y-6" stagger staggerDelay={0.05}>
      <PageTransitionItem>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-600 hover:text-purple-700"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to Operations
        </button>
      </PageTransitionItem>

      <PageTransitionItem>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Encounter</h1>
          <p className="mt-2 font-mono text-sm text-gray-500">{encounterId}</p>
        </div>
      </PageTransitionItem>

      {loading && (
        <PageTransitionItem>
          <div className="flex items-center justify-center py-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="h-10 w-10 rounded-full border-4 border-purple-600 border-t-transparent"
            />
          </div>
        </PageTransitionItem>
      )}
      {error && !loading && (
        <PageTransitionItem>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">{error}</div>
        </PageTransitionItem>
      )}

      {report && !loading && (
        <>
          <DiagnosisPanel findings={report.findings} healthyText="No issues detected for this encounter." />

          <PageTransitionItem>
            <AnimatedCard variant="gradient" hover={false}>
              <div className="p-5">
                <div className="mb-4 flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">Encounter</h2>
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${derivedStatusClass(report.encounter.derivedStatus)}`}
                  >
                    {report.encounter.derivedStatus}
                  </span>
                </div>
                <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                  <Field label="True status (derived)" value={report.encounter.derivedStatus} />
                  <Field label="Raw status (stale column)" value={report.encounter.rawStatus || '—'} tone="amber" />
                  <Field label="Organization" value={report.encounter.organizationName || '—'} />
                  <Field
                    label="Patient ID"
                    value={report.encounter.patientId || report.encounter.patientMrn || '—'}
                  />
                  <Field label="Provider" value={report.provider.name || '—'} />
                  <Field label="Provider email" value={report.provider.email || '—'} />
                  <Field label="Created" value={fmtDateTime(report.encounter.createdAt)} />
                  <Field label="Last updated" value={fmtDateTime(report.encounter.updatedAt)} />
                  <Field label="Signed" value={fmtDateTime(report.encounter.signedAt)} />
                  <Field label="EHR delivery" value={report.encounter.ehrDeliveryStatus || '—'} />
                  <Field label="Transcripts" value={String(report.encounter.transcriptCount)} />
                  <Field label="Retention expires" value={fmtDate(report.encounter.retentionExpiresAt)} />
                </div>
                {report.encounter.isDeleted && (
                  <p className="mt-3 text-sm font-medium text-red-700">This encounter is soft-deleted.</p>
                )}
                {report.provider.userId && (
                  <button
                    onClick={() => navigate(`/management/operations/user/${report.provider.userId}`)}
                    className="mt-4 text-sm font-medium text-purple-600 hover:text-purple-700"
                  >
                    View full report for {report.provider.name || 'this operator'} →
                  </button>
                )}
              </div>
            </AnimatedCard>
          </PageTransitionItem>

          <PageTransitionItem>
            <AnimatedCard variant="gradient" hover={false}>
              <div className="p-5">
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Recordings</h2>
                {report.recordings.length === 0 ? (
                  <EmptyRow text="No recordings linked to this encounter." />
                ) : (
                  <TableWrap headers={['Recording', 'Status', 'Retention', 'Soft-delete', 'Hard-delete']}>
                    {report.recordings.map((r) => (
                      <tr key={r.recordingId}>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">{shortId(r.recordingId)}</td>
                        <td className="px-4 py-2 text-gray-600">{r.status || '—'}</td>
                        <td className="px-4 py-2"><RetentionPill phase={r.retentionPhase} /></td>
                        <td className="px-4 py-2 text-gray-600">{fmtDate(r.softDeleteAt)}</td>
                        <td className="px-4 py-2 text-gray-600">{fmtDate(r.hardDeleteAt)}</td>
                      </tr>
                    ))}
                  </TableWrap>
                )}
              </div>
            </AnimatedCard>
          </PageTransitionItem>

          <PageTransitionItem>
            <AnimatedCard variant="gradient" hover={false}>
              <div className="p-5">
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Medical notes</h2>
                {report.notes.length === 0 ? (
                  <EmptyRow text="No medical notes linked to this encounter." />
                ) : (
                  <TableWrap headers={['Note', 'Patient ID', 'Status', 'Signed', 'Updated']}>
                    {report.notes.map((n) => (
                      <tr key={n.noteId}>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">{shortId(n.noteId)}</td>
                        <td className="px-4 py-2 text-gray-700">{n.patientId || n.patientMrn || '—'}</td>
                        <td className="px-4 py-2"><StatusPill status={n.status} /></td>
                        <td className="px-4 py-2 text-gray-600">{fmtDate(n.signedAt)}</td>
                        <td className="px-4 py-2 text-gray-600">{fmtDate(n.updatedAt)}</td>
                      </tr>
                    ))}
                  </TableWrap>
                )}
              </div>
            </AnimatedCard>
          </PageTransitionItem>

          <PageTransitionItem>
            <p className="text-center text-xs text-gray-400">
              Generated {fmtDateTime(report.generatedAt)} · PHI level: {report.meta.phiLevel} · read-only
            </p>
          </PageTransitionItem>
        </>
      )}
    </PageTransition>
  );
};

// ---------------------------------------------------------------------------
// Shared small components
// ---------------------------------------------------------------------------

const DiagnosisPanel: React.FC<{ findings: TriageFinding[]; healthyText?: string }> = ({ findings, healthyText }) => {
  const order: Record<TriageSeverity, number> = { blocker: 0, warning: 1, info: 2 };
  const sorted = [...findings].sort((a, b) => order[a.severity] - order[b.severity]);
  return (
    <PageTransitionItem>
      <AnimatedCard variant="gradient" hover={false}>
        <div className="p-5">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
            <ExclamationTriangleIcon className="h-5 w-5 text-purple-600" />
            Diagnosis
          </h2>
          {sorted.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              {healthyText ?? 'No issues detected — account and clinical workflow look healthy.'}
            </div>
          ) : (
            <div className="space-y-3">
              {sorted.map((f) => (
                <FindingCard key={f.ruleId} finding={f} />
              ))}
            </div>
          )}
        </div>
      </AnimatedCard>
    </PageTransitionItem>
  );
};

const FindingCard: React.FC<{ finding: TriageFinding }> = ({ finding }) => {
  const [open, setOpen] = useState(false);
  const style = SEVERITY_STYLE[finding.severity];
  const hasEvidence = finding.evidence && Object.keys(finding.evidence).length > 0;
  return (
    <div className={`rounded-lg border ${style.ring} p-4`}>
      <div className="flex items-start gap-3">
        <SeverityIcon severity={finding.severity} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{finding.title}</h3>
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${style.chip}`}>{style.label}</span>
          </div>
          <p className="mt-1 text-sm text-gray-700">{finding.explanation}</p>
          <p className="mt-2 text-sm">
            <span className="font-medium text-gray-900">Next step: </span>
            <span className="text-gray-700">{finding.suggestedNextStep}</span>
          </p>
          {hasEvidence && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="mt-2 text-xs font-medium text-purple-600 hover:text-purple-700"
            >
              {open ? 'Hide evidence' : 'Show evidence'}
            </button>
          )}
          <AnimatePresence>
            {open && hasEvidence && (
              <motion.pre
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 overflow-x-auto rounded bg-white/70 p-2 text-xs text-gray-600"
              >
                {JSON.stringify(finding.evidence, null, 2)}
              </motion.pre>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; tone?: 'red' | 'amber' | 'green' }> = ({
  label,
  value,
  tone,
}) => {
  const valueClass =
    tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : tone === 'green' ? 'text-green-700' : 'text-gray-900';
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-sm font-medium ${valueClass}`}>{value}</div>
    </div>
  );
};

const CountTile: React.FC<{ label: string; value: string; tone?: 'amber' }> = ({ label, value, tone }) => (
  <div className={`rounded-lg border px-4 py-3 ${tone === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}>
    <div className={`text-xl font-semibold ${tone === 'amber' ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

const TableWrap: React.FC<{ headers: string[]; children: React.ReactNode }> = ({ headers, children }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200">
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className="px-4 py-2">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">{children}</tbody>
    </table>
  </div>
);

const EmptyRow: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-sm text-gray-500">{text}</p>
);

export default Operations;
